// Modules
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

// Components
import { Page, PageHeader, Tabs, Tab } from 'components/page';
import { RoundLoader } from 'components/widgets/state-indicators/loader';
import { SVGButton } from 'components/widgets/buttons';
import ClosingLastTask from 'components/models/ticket/closing-last-task';
import SVG from 'components/widgets/icons/svg';
import TeamPhoneButton from 'components/widgets/buttons/team-phone-button';

// Actions
import * as timerActions from 'modules/timers/actions';

// Hooks
import { useCurrentUser } from 'hooks';
import useTicket from './hooks/useTicket';

function ViewTicket({ routeParams, children }) {
  const ticketId = routeParams.ticketId;
  const { currentUser } = useCurrentUser();
  // The page owns the socket subscription (socket: true) so the merge-refresh
  // runs once regardless of which tab is open; sub-routes share the cache.
  const {
    ticket = {},
    isFetching,
    files,
    isFetchingFiles,
    updateTask,
    updateTicket,
    saveNewTask
  } = useTicket(ticketId, { socket: true });

  const billable = _.get(ticket, 'agreement.id');

  const headerActions = (
    <div className="header-actions align-items-center">
      <TeamPhoneButton team={_.get(ticket, 'company.team')} label="Ticket Team Phone" />
      {billable &&
        <SVGButton
          onClick={() => timerActions.add(ticket)}
          title="Start Timer"
          placement="bottom"
          src="svg/play.svg"
          className="bg-asbestos text-white rounded-circle p-2 mr-2 ml-3"
        />
      }
    </div>
  );

  return (
    <Page className="ticket-page">

      {ticket.showClosingLastTaskModal &&
        <ClosingLastTask
          ticket={ticket}
          updateTask={updateTask}
          saveNewTask={saveNewTask}
          updateTicket={updateTicket}
          currentUser={currentUser}
        />
      }
      <PageHeader
        title={!isFetching ? ticket.description : ''}
        actions={headerActions}
      >
        <Tabs className="actions-tabs">
          <Tab to={`/tickets/${ticketId}/summary`}>
            <SVG src="svg/ticket.svg" className="ticket" />
            <span>Summary</span>
          </Tab>
          <Tab to={`/tickets/${ticketId}/tasks`}>
            <SVG src="svg/task.svg" className="task-icon" />
            <span>Tasks</span>
          </Tab>
          <Tab to={`/tickets/${ticketId}/time`}>
            <SVG src="svg/time-report.svg" className="time-report" />
            <span>Time</span>
          </Tab>
          <Tab to={`/tickets/${ticketId}/expenses`}>
            <SVG src="svg/expense-bill.svg" className="expense-bill" />
            <span>Purchases</span>
          </Tab>
          <Tab to={`/tickets/${ticketId}/emails`}>
            <SVG src="svg/email.svg" className="email" />
            <span>Emails</span>
          </Tab>
          <Tab to={`/tickets/${ticketId}/files`}>
            <SVG src="svg/google-drive.svg" className="google-drive" />
            <span>Files</span>
            {isFetchingFiles && <RoundLoader color="#34495e" />}
            {!isFetchingFiles && ticket.folder && <span> ({_.get(files, 'length', 0)})</span>}
          </Tab>
        </Tabs>
      </PageHeader>
      {!isFetching && children}
    </Page>
  );
}

ViewTicket.propTypes = {
  routeParams: PropTypes.object,
  children: PropTypes.node
};

export default ViewTicket;
